import React, { useState } from 'react';
import { API_BASE_URL } from '../constants';
import authUtils from '../utils/authUtils';

const MessageFeedback = ({ messageId, existingFeedback, onFeedbackUpdate, isFinal }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  
  // Check if feedback actually exists (not just an empty object)
  const hasFeedback = existingFeedback && 
    existingFeedback.id && 
    (existingFeedback.is_helpful !== undefined && existingFeedback.is_helpful !== null);

  const [feedbackData, setFeedbackData] = useState({
    is_helpful: hasFeedback ? existingFeedback.is_helpful : null,
    reason: hasFeedback ? existingFeedback.reason || '' : '',
    comment: hasFeedback ? existingFeedback.comment || '' : ''
  });

  console.log('MessageFeedback debug:', {
    messageId,
    existingFeedback,
    hasFeedback,
    feedbackData
  });

  const feedbackReasons = [
    { value: 'incorrect_data', label: 'Некорректные данные' },
    { value: 'incomplete_answer', label: 'Недостаточно полный ответ' },
    { value: 'misunderstood', label: 'Ассистент не понял вопрос' },
    { value: 'other', label: 'Другое' }
  ];

  const handleHelpfulChoice = (isHelpful) => {
    setFeedbackData(prev => ({ ...prev, is_helpful: isHelpful }));
    
    if (isHelpful) {
      // For positive feedback, submit immediately
      handleSubmit(isHelpful);
    } else {
      // For negative feedback, show additional fields
      setShowDetails(true);
    }
  };

  const handleSubmit = async (isHelpful = null) => {
    try {
      setIsSubmitting(true);
      
      const encodedCredentials = authUtils.getEncodedCredentials();
      if (!encodedCredentials) {
        throw new Error('No authentication credentials found');
      }

      const finalIsHelpful = isHelpful !== null ? isHelpful : feedbackData.is_helpful;
      
      const requestData = {
        message_id: messageId,
        is_helpful: finalIsHelpful,
        reason: !finalIsHelpful && feedbackData.reason ? feedbackData.reason : null,
        comment: feedbackData.comment || null
      };

      const response = await fetch(`${API_BASE_URL}/feedbacks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Credentials': encodedCredentials,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit feedback: ${response.status}`);
      }

      const data = await response.json();
      
      // Update the feedback data with the response
      const updatedFeedback = {
        id: data.data.id,
        message_id: data.data.message_id,
        is_helpful: data.data.is_helpful,
        reason: data.data.reason,
        comment: data.data.comment,
        created_at: data.data.created_at
      };

      setFeedbackData({
        is_helpful: updatedFeedback.is_helpful,
        reason: updatedFeedback.reason || '',
        comment: updatedFeedback.comment || ''
      });

      setShowForm(false);
      setShowDetails(false);
      
      if (onFeedbackUpdate) {
        onFeedbackUpdate(updatedFeedback);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert(`Ошибка при отправке фидбека: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!existingFeedback?.id) return;

    try {
      setIsSubmitting(true);
      
      const encodedCredentials = authUtils.getEncodedCredentials();
      if (!encodedCredentials) {
        throw new Error('No authentication credentials found');
      }

      const response = await fetch(`${API_BASE_URL}/feedbacks/${existingFeedback.id}`, {
        method: 'DELETE',
        headers: {
          'X-User-Credentials': encodedCredentials,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete feedback: ${response.status}`);
      }

      setFeedbackData({
        is_helpful: null,
        reason: '',
        comment: ''
      });

      setShowForm(false);
      setShowDetails(false);
      
      if (onFeedbackUpdate) {
        onFeedbackUpdate(null);
      }
    } catch (error) {
      console.error('Error deleting feedback:', error);
      alert(`Ошибка при удалении фидбека: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFeedbackText = (feedback) => {
    if (!feedback) return '';
    
    if (feedback.is_helpful) {
      return '👍 Полезно';
    } else {
      const reasonText = feedback.reason ? 
        feedbackReasons.find(r => r.value === feedback.reason)?.label || feedback.reason 
        : 'Не указано';
      return `👎 Не полезно (${reasonText})`;
    }
  };

  const getFeedbackComment = (feedback) => {
    if (!feedback || !feedback.comment) return '';
    return feedback.comment;
  };

  if (isFinal !== true) {
    return null;
  }

  // If feedback exists and we're not showing form, display existing feedback
  if (hasFeedback && !showForm) {
    return (
      <div className="message-feedback existing">
        <div className="feedback-display">
          <span className="feedback-text">{getFeedbackText(existingFeedback)}</span>
          {getFeedbackComment(existingFeedback) && (
            <div className="feedback-comment">{getFeedbackComment(existingFeedback)}</div>
          )}
          <div className="feedback-actions">
            <button 
              onClick={() => {
                setShowForm(true);
                setShowDetails(false);
                setFeedbackData({
                  is_helpful: null, // Reset to allow new choice
                  reason: '', // Clear reason
                  comment: '' // Clear comment
                });
              }}
              className="edit-feedback-btn"
              disabled={isSubmitting}
            >
              Изменить
            </button>
            <button 
              onClick={handleDelete}
              className="delete-feedback-btn"
              disabled={isSubmitting}
            >
              Удалить
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If no feedback exists and we're not showing form, show a simple button to add feedback
  if (!hasFeedback && !showForm) {
    return (
      <div className="message-feedback">
        <button 
          onClick={() => {
            setShowForm(true);
            setShowDetails(false);
            setFeedbackData({
              is_helpful: null,
              reason: '',
              comment: ''
            });
          }}
          className="add-feedback-btn"
          disabled={isSubmitting}
        >
          Оставить фидбек
        </button>
      </div>
    );
  }

  // Show feedback form (either for new feedback or editing existing)
  return (
    <div className="message-feedback form">
      <div className="feedback-form">
        <div className="feedback-question">
          <span>{existingFeedback ? 'Изменить фидбек:' : 'Был ли этот ответ полезным?'}</span>
        </div>
        
        <div className="feedback-buttons">
          <button
            onClick={() => handleHelpfulChoice(true)}
            className={`feedback-btn helpful ${feedbackData.is_helpful === true ? 'selected' : ''}`}
            disabled={isSubmitting}
          >
            👍 Да, полезно
          </button>
          <button
            onClick={() => handleHelpfulChoice(false)}
            className={`feedback-btn not-helpful ${feedbackData.is_helpful === false ? 'selected' : ''}`}
            disabled={isSubmitting}
          >
            👎 Нет, не полезно
          </button>
        </div>

        {showDetails && feedbackData.is_helpful === false && (
          <div className="feedback-details">
            <div className="reason-selection">
              <label htmlFor="reason">Причина:</label>
              <select
                id="reason"
                value={feedbackData.reason}
                onChange={(e) => setFeedbackData(prev => ({ ...prev, reason: e.target.value }))}
                disabled={isSubmitting}
              >
                <option value="">Выберите причину</option>
                {feedbackReasons.map(reason => (
                  <option key={reason.value} value={reason.value}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="comment-input">
              <label htmlFor="comment">Комментарий (необязательно):</label>
              <textarea
                id="comment"
                value={feedbackData.comment}
                onChange={(e) => setFeedbackData(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="Дополнительные комментарии..."
                rows={2}
                disabled={isSubmitting}
              />
            </div>
            
            <div className="feedback-submit-actions">
              <button
                onClick={() => handleSubmit()}
                className="submit-feedback-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Отправка...' : 'Отправить фидбек'}
              </button>
            </div>
          </div>
        )}

        <div className="feedback-form-actions">
          <button
            onClick={() => {
              setShowForm(false);
              setShowDetails(false);
              setFeedbackData({
                is_helpful: hasFeedback ? existingFeedback.is_helpful : null,
                reason: hasFeedback ? existingFeedback.reason || '' : '',
                comment: hasFeedback ? existingFeedback.comment || '' : ''
              });
            }}
            className="cancel-btn"
            disabled={isSubmitting}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageFeedback;
